import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch, IconInbox, IconLock, IconChevronRight } from '../icons';
import { Link } from 'react-router-dom';
import {
  collection, getDocs, query,
  where, orderBy, deleteDoc, doc, setDoc, Timestamp,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { ReceivedInvoice, ParsedZugferdData } from '../types/receivedInvoiceTypes';
import ReceivedInvoiceUpload, { ReceivedInvoiceUploadMeta } from '../components/receivedInvoices/ReceivedInvoiceUpload';
import ReceivedInvoiceCard from '../components/receivedInvoices/ReceivedInvoiceCard';
import ReceivedInvoiceDetail from '../components/receivedInvoices/ReceivedInvoiceDetail';
import { getExpensesForUser } from '../api/expensesApi';
import ExpenseBookingModal from '../components/expenses/ExpenseBookingModal';
import { canUseInbound } from '../config/inboundAccess';
import { buildUpgradeHref } from '../config/nudgePromotions';
import analyticsService from '../services/analyticsService';

// ── Feature flag (fail-closed: default false unless explicitly 'true') ────────
const EXPENSES_ENABLED = process.env.REACT_APP_EXPENSES_ENABLED === 'true';

const ReceivedInvoicesPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, isPremium, effectiveScopeUid } = useAuth();
  // M11b-3: advisor (read-only) darf keine Eingangsrechnung hochladen/löschen/buchen.
  const isReadOnly = useIsReadOnly();
  // fix-advisor-nav-curation: sprechender Alias für den Premium-Bypass (Abschnitt D).
  // Identisch zu isReadOnly — der StB soll die Upsell-Sperre NIE sehen, auch wenn er
  // selbst nicht premium ist (kein Premium-Grant, nur die kosmetische Sperre entfällt).
  const isAdvisorViewingClient = isReadOnly;

  const [invoices, setInvoices] = useState<ReceivedInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<ReceivedInvoice | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Expense dedup state (flag-gated) ──────────────────────────────────────
  /**
   * Set of received-invoice IDs that already have an expense entry.
   * Loaded once on mount (when flag is active), then updated optimistically
   * after a successful booking so the UI reflects the change immediately.
   */
  const [expensedInvoiceIds, setExpensedInvoiceIds] = useState<Set<string>>(new Set());
  const [bookingInvoice, setBookingInvoice] = useState<ReceivedInvoice | null>(null);

  // ── Load from Firestore ─────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || (!isPremium && !isAdvisorViewingClient)) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const q = query(
          collection(db, 'received_invoices'),
          where('userId', '==', effectiveScopeUid),
          orderBy('uploadedAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const docs = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<ReceivedInvoice, 'id'>),
        })) as ReceivedInvoice[];
        setInvoices(docs);
      } catch (err) {
        console.error('Error loading received invoices:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, effectiveScopeUid, isPremium, isAdvisorViewingClient]);

  // ── Load already-expensed invoice IDs (flag-gated) ───────────────────────
  /**
   * Loads all expenses for the current user and builds a Set of
   * sourceReceivedInvoiceIds for O(1) dedup lookup.
   * Only runs when EXPENSES_ENABLED and user is authenticated + premium.
   */
  useEffect(() => {
    if (!EXPENSES_ENABLED || !currentUser || (!isPremium && !isAdvisorViewingClient)) return;

    let cancelled = false;

    const loadExpensedIds = async () => {
      try {
        const expenses = await getExpensesForUser(effectiveScopeUid);
        if (!cancelled) {
          const ids = new Set(
            expenses
              .map((e) => e.sourceReceivedInvoiceId)
              .filter((id): id is string => id !== null),
          );
          setExpensedInvoiceIds(ids);
        }
      } catch (err) {
        // Non-critical: dedup display may be stale, but booking still works
        console.error('ReceivedInvoicesPage: failed to load expensed IDs', err);
      }
    };

    loadExpensedIds();
    return () => { cancelled = true; };
  }, [currentUser, effectiveScopeUid, isPremium, isAdvisorViewingClient]);

  // ── Save new invoice after upload ───────────────────────────────────────
  /**
   * IPA-3: `meta.receivedInvoiceId` is pre-allocated by `ReceivedInvoiceUpload`
   * (uniform signature regardless of the archive flag), so we `setDoc` at that
   * exact ID instead of `addDoc` — verhaltensneutral when `pdfStorageRef` is
   * `null` (flag off / upload failed), which is why the three PDF-audit
   * fields are only included in the write when a ref is actually present.
   */
  const handleUploadSuccess = async (data: ParsedZugferdData, meta: ReceivedInvoiceUploadMeta) => {
    if (!currentUser) return;
    const { filename, receivedInvoiceId, pdfStorageRef, byteSize } = meta;
    const uploadedAt = new Date().toISOString();
    try {
      const docPayload: Record<string, unknown> = {
        userId: effectiveScopeUid,
        uploadedAt,
        filename,
        data,
      };
      if (pdfStorageRef) {
        docPayload.pdfStorageRef = pdfStorageRef;
        docPayload.pdfUploadedAt = uploadedAt;
        if (byteSize !== null) docPayload.pdfByteSize = byteSize;
      }
      await setDoc(doc(db, 'received_invoices', receivedInvoiceId), docPayload);
      const newInvoice: ReceivedInvoice = {
        id: receivedInvoiceId,
        userId: effectiveScopeUid,
        uploadedAt,
        filename,
        data,
        ...(pdfStorageRef ? { pdfStorageRef, pdfUploadedAt: uploadedAt } : {}),
        ...(pdfStorageRef && byteSize !== null ? { pdfByteSize: byteSize } : {}),
      };
      setInvoices((prev) => [newInvoice, ...prev]);
      setSelectedInvoice(newInvoice);
      toast.success(t('receivedInvoices.savedSuccess', 'Rechnung gespeichert'));
    } catch (err) {
      console.error('Error saving received invoice:', err);
      toast.error(t('error', 'Fehler beim Speichern'));
    }
  };

  // ── Loop-A weiche (§5.11): server WORM ingest success ────────────────────
  /**
   * Called when `INBOUND_ENABLED` is on and the upload went through the server
   * WORM pipeline (`ingestInboundUpload` → `inbound_documents`). The new doc
   * appears in the Posteingang, NOT in this legacy list — so we only toast and
   * point the user there. No `received_invoices` write here (no double archive).
   */
  const handleInboundUploadSuccess = useCallback(
    (result: { inboundDocumentId: string; deferred: boolean }) => {
      toast.success(
        result.deferred
          ? t(
              'receivedInvoices.inboundDeferred',
              'Sicher archiviert — die Auswertung folgt. Sie finden den Beleg im Posteingang.',
            )
          : t(
              'receivedInvoices.inboundArchived',
              'Im Posteingang archiviert. Die Auswertung finden Sie dort.',
            ),
      );
    },
    [t],
  );

  // ── Delete invoice ──────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'received_invoices', id));
      setInvoices((prev) => prev.filter((inv) => inv.id !== id));
      toast.success(t('receivedInvoices.deletedSuccess', 'Rechnung entfernt'));
    } catch (err) {
      console.error('Error deleting received invoice:', err);
      toast.error(t('error', 'Fehler beim Löschen'));
    }
  };

  // ── Expense booking handlers (flag-gated) ────────────────────────────────
  const handleBookExpense = useCallback((invoice: ReceivedInvoice) => {
    setBookingInvoice(invoice);
  }, []);

  const handleExpenseBooked = useCallback((expenseId: string) => {
    if (bookingInvoice) {
      // Optimistic update: mark this invoice as expensed immediately
      setExpensedInvoiceIds((prev) => new Set([...Array.from(prev), bookingInvoice.id]));
    }
    setBookingInvoice(null);
  }, [bookingInvoice]);

  const handleModalClose = useCallback(() => {
    setBookingInvoice(null);
  }, []);

  // ── Search filter ───────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return invoices;
    const q = searchQuery.toLowerCase();
    return invoices.filter(({ data, filename }) =>
      (data.documentNo || '').toLowerCase().includes(q) ||
      (data.sellerName || '').toLowerCase().includes(q) ||
      (data.buyerName || '').toLowerCase().includes(q) ||
      (data.sellerCity || '').toLowerCase().includes(q) ||
      (data.dueDate || '').includes(q) ||
      (data.documentDate || '').includes(q) ||
      (filename || '').toLowerCase().includes(q)
    );
  }, [invoices, searchQuery]);

  // ── M-N5 Nudge-Telemetrie (Lock-Card, architecture-nudge.md §7.4) ─────────
  // `view_promotion` feuert 1x pro Mount + Flankenwechsel, wenn die
  // Premium-Sperre sichtbar wird. Hook läuft UNBEDINGT vor dem
  // Premium-Early-Return (Rules of Hooks) — der eigentliche Guard lebt im
  // Effekt selbst.
  const showPremiumLock = !isPremium && !isAdvisorViewingClient;
  useEffect(() => {
    if (!showPremiumLock) return;
    analyticsService.trackViewPromotion({ promotion_id: 'feature_lock_received_invoices' });
  }, [showPremiumLock]);

  const handleUpgradeLinkClick = useCallback(() => {
    analyticsService.trackSelectPromotion({ promotion_id: 'feature_lock_received_invoices' });
  }, []);

  // ── Premium gate ────────────────────────────────────────────────────────
  // fix-advisor-nav-curation Abschnitt D: der StB soll die Upsell-Sperre im
  // Advisor-Kontext NIE sehen (Mandant kann premium sein, StB selbst nicht).
  // KEIN Premium-Grant — nur die kosmetische Sperre entfällt für den StB.
  if (showPremiumLock) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-surface-raised border border-hairline rounded-2xl p-8 text-center shadow-xl">
          <div className="flex justify-center mb-5">
            <div className="bg-[var(--tone-accent-bg)] rounded-full p-4">
              <IconLock className="text-accent-ink" size={30} />
            </div>
          </div>
          <h2 className="text-xl font-bold text-primary mb-3">
            {t('receivedInvoices.premiumTitle', 'Premium-Funktion')}
          </h2>
          <p className="text-muted mb-6 leading-relaxed">
            {t(
              'receivedInvoices.premiumMessage',
              'Das Einlesen von ZUGFeRD-Eingangsrechnungen ist ausschließlich für Premium-Nutzer verfügbar. Erhalte sofortigen Zugriff und nutze alle Vorteile von FaeBill Premium.'
            )}
          </p>
          <Link
            to={buildUpgradeHref('feature_lock_received_invoices')}
            onClick={handleUpgradeLinkClick}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-[#0C1322] font-semibold px-6 py-3 rounded-xl transition-colors"
          >
            {t('receivedInvoices.premiumCta', 'Jetzt Premium werden')}
            <IconChevronRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  // ── Detail view ─────────────────────────────────────────────────────────
  if (selectedInvoice) {
    return (
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-5xl mx-auto">
          <ReceivedInvoiceDetail
            invoice={selectedInvoice}
            onBack={() => setSelectedInvoice(null)}
            // F4 (architecture-goal-durchstich-fixplan.md §1 A1): same gate as the
            // list-view ReceivedInvoiceCard.onBookExpense below — no new
            // permission model.
            canBookExpense={EXPENSES_ENABLED && !isReadOnly && !expensedInvoiceIds.has(selectedInvoice.id)}
            onBookExpense={handleBookExpense}
            // F4-reloop: same dedup Set the list-view card's `isExpensed` uses.
            alreadyExpensed={EXPENSES_ENABLED && expensedInvoiceIds.has(selectedInvoice.id)}
          />
        </div>
        {/* BUG-1 (tester F4): this detail branch `return`s before the list-view's
            bottom modal mounts, so the booking modal needs its own instance here
            too — exactly like UnifiedInboxPage's received-detail branch. Without
            this the "Als Ausgabe buchen" button set `bookingInvoice` but nothing
            ever rendered. */}
        {EXPENSES_ENABLED && bookingInvoice && (
          <ExpenseBookingModal
            open={true}
            invoice={bookingInvoice}
            onClose={handleModalClose}
            onBooked={handleExpenseBooked}
          />
        )}
      </div>
    );
  }

  // ── List view ───────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-surface p-4 md:p-6">
        <div className="max-w-5xl mx-auto">
          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
              <IconInbox className="text-accent-ink" size={24} />
              {t('receivedInvoices.title', 'Eingangsrechnungen')}
            </h1>
            <p className="text-muted text-sm mt-1">
              {t(
                'receivedInvoices.subtitle',
                'ZUGFeRD- und XRechnung-PDFs einlesen und verwalten'
              )}
            </p>
          </div>

          {/* Loop-A: Hinweis auf den neuen Posteingang; die Alt-Liste bleibt für
              received_invoices-Altbestand (Alt-Erfassung ohne Originaldatei). */}
          {canUseInbound(effectiveScopeUid) && (
            <div className="mb-6 bg-[var(--tone-accent-bg)] border border-[var(--tone-accent-border)] rounded-xl p-4 text-sm text-[var(--tone-accent-fg)] flex items-start gap-3">
              <IconInbox className="mt-0.5 flex-shrink-0 text-accent-ink" size={14} />
              <div>
                <p className="font-medium text-primary">
                  {t('receivedInvoices.legacyBannerTitle', 'Alt-Erfassung')}
                </p>
                <p className="mt-1 text-[var(--tone-accent-fg)]">
                  {t(
                    'receivedInvoices.legacyBannerBody',
                    'Diese Seite zeigt frühere Erfassungen ohne Originaldatei. Neue Belege werden im Posteingang archiviert.',
                  )}
                </p>
                <Link
                  to="/dashboard/posteingang"
                  className="inline-flex items-center gap-1 mt-2 text-accent-ink hover:text-indigo-200 font-medium"
                >
                  {t('receivedInvoices.legacyBannerLink', 'Zum Posteingang')}
                  <IconChevronRight size={12} />
                </Link>
              </div>
            </div>
          )}

          {/* Upload area — M11b-3: für advisor (read-only) ausgeblendet (Upload = mutierend) */}
          {!isReadOnly && (
            <ReceivedInvoiceUpload
              onUploadSuccess={handleUploadSuccess}
              onInboundUploadSuccess={handleInboundUploadSuccess}
            />
          )}

          {/* Search */}
          {invoices.length > 0 && (
            <div className="relative mb-5">
              <IconSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" size={14} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('receivedInvoices.searchPlaceholder', 'Suchen nach Absender, Rechnungsnummer, Datum...')}
                className="w-full bg-surface-raised border border-hairline focus:border-indigo-500 rounded-xl pl-10 pr-4 py-2.5 text-primary text-sm placeholder:text-faint outline-none transition-colors"
              />
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-16 text-muted text-sm">
              {t('loading', 'Wird geladen...')}
            </div>
          )}

          {/* Empty state */}
          {!loading && invoices.length === 0 && (
            <div className="text-center py-16">
              <IconInbox className="text-gray-600 mx-auto mb-4" size={48} />
              <p className="text-muted font-medium">
                {t('receivedInvoices.noInvoices', 'Noch keine Eingangsrechnungen')}
              </p>
              <p className="text-faint text-sm mt-1">
                {t(
                  'receivedInvoices.noInvoicesHint',
                  'Laden Sie oben eine ZUGFeRD / XRechnung PDF hoch.'
                )}
              </p>
            </div>
          )}

          {/* No results */}
          {!loading && invoices.length > 0 && filtered.length === 0 && (
            <div className="text-center py-12 text-faint text-sm">
              {t('receivedInvoices.noResults', 'Keine Rechnungen gefunden für')} „{searchQuery}"
            </div>
          )}

          {/* Invoice grid */}
          {filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map((inv) => (
                <ReceivedInvoiceCard
                  key={inv.id}
                  invoice={inv}
                  onClick={setSelectedInvoice}
                  onDelete={canUseInbound(effectiveScopeUid) ? undefined : handleDelete}
                  onBookExpense={EXPENSES_ENABLED && !isReadOnly ? handleBookExpense : undefined}
                  isExpensed={EXPENSES_ENABLED ? expensedInvoiceIds.has(inv.id) : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Expense Booking Modal (flag-gated — only mounts when there's an invoice to book) */}
      {EXPENSES_ENABLED && bookingInvoice && (
        <ExpenseBookingModal
          open={true}
          invoice={bookingInvoice}
          onClose={handleModalClose}
          onBooked={handleExpenseBooked}
        />
      )}
    </>
  );
};

export default ReceivedInvoicesPage;
